import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Msg91OtpWidget, {
  isMsg91WidgetConfigured,
} from '../../components/Msg91OtpWidget';
import { getApiBaseUrl } from '../../services/httpClient';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Divider,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Pin as PinIcon,
  Visibility,
  VisibilityOff,
  ConfirmationNumber as OtpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';

const API_BASE = `${getApiBaseUrl()}/franchisee/admin/forgot-pin`;

const apiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && data.message) return String(data.message);
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) {
    return data.trim();
  }
  const status = err?.response?.status;
  if (status >= 500) {
    return 'Could not send OTP — server temporarily unavailable. Please try again in a minute.';
  }
  if (!err?.response) {
    return 'Could not reach API. Check your connection and try again.';
  }
  return fallback;
};

/**
 * Mitra reset PIN via mobile OTP (locsydashboard/forgot-pin).
 * Uses MSG91 widget when configured (same as register / forgot-password).
 */
const ForgotPin = () => {
  const navigate = useNavigate();
  const useMsg91Widget = isMsg91WidgetConfigured();
  const stepLabels = useMemo(
    () =>
      useMsg91Widget
        ? ['Phone number', 'New PIN']
        : ['Phone number', 'OTP', 'New PIN'],
    [useMsg91Widget],
  );
  const successStepIndex = stepLabels.length;

  const [activeStep, setActiveStep] = useState(0);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [msg91AccessToken, setMsg91AccessToken] = useState('');
  const [msg91RequestUserId, setMsg91RequestUserId] = useState('');
  const lastPhoneForMsg91Ref = useRef('');
  const phoneDigitsRef = useRef('');

  const step1Formik = useFormik({
    initialValues: { phone: '' },
    validationSchema: yup.object({
      phone: yup
        .string()
        .required('Phone number is required')
        .matches(/^[6-9]\d{9}$/, 'Invalid phone number (10 digits)'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      phoneDigitsRef.current = values.phone;

      try {
        if (useMsg91Widget && !msg91AccessToken) {
          setError(
            'Enter your mobile number, complete verification below, then tap Continue.',
          );
          return;
        }

        const response = await axios.post(`${API_BASE}/step1`, {
          phone: values.phone,
          ...(useMsg91Widget ? { skip_managed_otp: true } : {}),
        });

        if (response.data?.success) {
          setPhone(values.phone);
          setActiveStep(1);
        } else {
          setError(response.data?.message || 'Could not send OTP');
        }
      } catch (err) {
        setError(apiErrorMessage(err, 'Could not send OTP'));
      } finally {
        setLoading(false);
      }
    },
  });

  const step2Formik = useFormik({
    initialValues: { otp: '' },
    validationSchema: yup.object({
      otp: yup
        .string()
        .required('OTP is required')
        .matches(/^\d{6}$/, 'OTP must be 6 digits'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.post(`${API_BASE}/verify-otp`, {
          phone,
          otp: values.otp,
        });
        if (response.data?.success) {
          setOtp(values.otp);
          setActiveStep(2);
        } else {
          setError(response.data?.message || 'Invalid OTP');
        }
      } catch (err) {
        setError(apiErrorMessage(err, 'OTP verification failed'));
      } finally {
        setLoading(false);
      }
    },
  });

  const step3Formik = useFormik({
    initialValues: { new_pin: '', confirm_pin: '' },
    validationSchema: yup.object({
      new_pin: yup
        .string()
        .required('PIN is required')
        .matches(/^\d{4,9}$/, 'PIN must be 4 to 9 digits')
        .test(
          'not-last4',
          'Do not use the last 4 digits of your phone as your PIN',
          (value) => {
            const last4 = String(phoneDigitsRef.current || phone || '').slice(-4);
            return !(last4 && String(value) === last4);
          },
        ),
      confirm_pin: yup
        .string()
        .required('Confirm PIN is required')
        .oneOf([yup.ref('new_pin')], 'PINs must match'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        const payload = {
          phone: phone || step1Formik.values.phone,
          new_pin: values.new_pin,
          confirm_pin: values.confirm_pin,
        };
        if (useMsg91Widget) {
          payload.msg91_access_token = msg91AccessToken;
          if (msg91RequestUserId) {
            payload.msg91_request_user_id = msg91RequestUserId;
          }
        } else {
          payload.otp = otp;
        }

        const response = await axios.post(`${API_BASE}/step2`, payload);
        if (response.data?.success) {
          setActiveStep(successStepIndex);
        } else {
          setError(response.data?.message || 'Failed to reset PIN');
        }
      } catch (err) {
        setError(apiErrorMessage(err, 'Failed to reset PIN'));
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!useMsg91Widget) return;
    const nextPhone = step1Formik.values.phone || '';
    if (lastPhoneForMsg91Ref.current !== nextPhone) {
      lastPhoneForMsg91Ref.current = nextPhone;
      setMsg91AccessToken('');
      setMsg91RequestUserId('');
    }
  }, [step1Formik.values.phone, useMsg91Widget]);

  const handleBack = () => {
    if (activeStep <= 0) return;
    setError('');
    if (useMsg91Widget) {
      if (activeStep === 1) step3Formik.resetForm();
    } else {
      if (activeStep === 1) {
        step2Formik.resetForm({ values: { otp: '' } });
      }
      if (activeStep === 2) step3Formik.resetForm();
    }
    setActiveStep(activeStep - 1);
  };

  const renderPhoneStep = () => (
    <Box component="form" onSubmit={step1Formik.handleSubmit}>
      <TextField
        fullWidth
        label="Mobile number"
        name="phone"
        value={step1Formik.values.phone}
        onChange={(e) =>
          step1Formik.setFieldValue(
            'phone',
            String(e.target.value || '').replace(/\D/g, '').slice(0, 10),
          )
        }
        onBlur={step1Formik.handleBlur}
        error={step1Formik.touched.phone && Boolean(step1Formik.errors.phone)}
        helperText={
          (step1Formik.touched.phone && step1Formik.errors.phone) ||
          (useMsg91Widget
            ? 'Use the number linked to your Mitra account. Verify it below.'
            : 'Use the number linked to your Mitra account. We will send a 6-digit OTP.')
        }
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
      />

      {useMsg91Widget ? (
        <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'stretch', mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Verify mobile
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            Request the code, enter it in the widget, then tap Continue once it shows verified.
          </Typography>
          <Msg91OtpWidget
            phoneDigits10={step1Formik.values.phone}
            disabled={loading}
            onVerified={(token, meta) => {
              setMsg91AccessToken(token);
              const sessionId =
                meta && String(meta.requestUserId || meta.reqId || '').trim();
              setMsg91RequestUserId(sessionId || '');
              setError('');
            }}
            onError={(msg) => {
              setMsg91AccessToken('');
              setMsg91RequestUserId('');
              setError(msg || 'Verification failed');
            }}
          />
        </Stack>
      ) : null}

      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ py: 1.4 }}>
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : useMsg91Widget ? (
          'Continue'
        ) : (
          'Send OTP'
        )}
      </Button>
    </Box>
  );

  const renderOtpStep = () => (
    <Box component="form" onSubmit={step2Formik.handleSubmit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the 6-digit code sent to {phone}.
      </Typography>
      <TextField
        fullWidth
        label="6-digit OTP"
        name="otp"
        value={step2Formik.values.otp}
        onChange={(e) =>
          step2Formik.setFieldValue(
            'otp',
            String(e.target.value || '').replace(/\D/g, '').slice(0, 6),
          )
        }
        onBlur={step2Formik.handleBlur}
        error={step2Formik.touched.otp && Boolean(step2Formik.errors.otp)}
        helperText={step2Formik.touched.otp && step2Formik.errors.otp}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <OtpIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={handleBack} disabled={loading}>
          Back
        </Button>
        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ py: 1.4 }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify OTP'}
        </Button>
      </Box>
      <Button
        fullWidth
        disabled={loading}
        onClick={() => step1Formik.handleSubmit()}
        sx={{ mt: 1 }}
      >
        Resend OTP
      </Button>
    </Box>
  );

  const renderPinStep = () => (
    <Box component="form" onSubmit={step3Formik.handleSubmit}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a new 4–9 digit login PIN and confirm it.
      </Typography>
      <TextField
        fullWidth
        label="New PIN"
        name="new_pin"
        type={showPin ? 'text' : 'password'}
        value={step3Formik.values.new_pin}
        onChange={(e) =>
          step3Formik.setFieldValue(
            'new_pin',
            String(e.target.value || '').replace(/\D/g, '').slice(0, 9),
          )
        }
        onBlur={step3Formik.handleBlur}
        error={step3Formik.touched.new_pin && Boolean(step3Formik.errors.new_pin)}
        helperText={
          (step3Formik.touched.new_pin && step3Formik.errors.new_pin) || '4–9 digits'
        }
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PinIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPin((v) => !v)} edge="end">
                {showPin ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{ maxLength: 9, inputMode: 'numeric' }}
      />
      <TextField
        fullWidth
        label="Confirm PIN"
        name="confirm_pin"
        type={showPin ? 'text' : 'password'}
        value={step3Formik.values.confirm_pin}
        onChange={(e) =>
          step3Formik.setFieldValue(
            'confirm_pin',
            String(e.target.value || '').replace(/\D/g, '').slice(0, 9),
          )
        }
        onBlur={step3Formik.handleBlur}
        error={
          step3Formik.touched.confirm_pin && Boolean(step3Formik.errors.confirm_pin)
        }
        helperText={step3Formik.touched.confirm_pin && step3Formik.errors.confirm_pin}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PinIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{ maxLength: 9, inputMode: 'numeric' }}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={handleBack} disabled={loading}>
          Back
        </Button>
        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ py: 1.4 }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Save new PIN'}
        </Button>
      </Box>
    </Box>
  );

  const renderSuccess = () => (
    <Box sx={{ textAlign: 'center' }}>
      <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
      <Typography variant="h6" gutterBottom>
        PIN reset successful
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You can now sign in with phone + PIN.
      </Typography>
      <Button
        fullWidth
        variant="contained"
        onClick={() => navigate('/locsydashboard/login')}
      >
        Back to login
      </Button>
    </Box>
  );

  const renderStepContent = () => {
    if (activeStep === successStepIndex) return renderSuccess();
    if (activeStep === 0) return renderPhoneStep();
    if (useMsg91Widget && activeStep === 1) return renderPinStep();
    if (!useMsg91Widget && activeStep === 1) return renderOtpStep();
    if (!useMsg91Widget && activeStep === 2) return renderPinStep();
    return null;
  };

  const stepperActiveStep =
    activeStep >= successStepIndex ? stepLabels.length : activeStep;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={8} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Reset login PIN
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {useMsg91Widget
              ? 'Confirm the mobile linked to your Mitra account, then set a new 4–9 digit PIN.'
              : 'Use the mobile number linked to your Mitra account. We will send an OTP to set a new 4–9 digit PIN.'}
          </Typography>

          {activeStep < successStepIndex && (
            <Stepper activeStep={stepperActiveStep} sx={{ mb: 3 }}>
              {stepLabels.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          {activeStep < successStepIndex && (
            <Typography variant="body2" align="center" sx={{ mt: 3 }}>
              <Link to="/locsydashboard/login" style={{ color: '#667eea' }}>
                Back to login
              </Link>
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPin;
